import DOMPurify from "dompurify";

type SanitizeFn = (dirty: string, options?: Record<string, unknown>) => string;

let cachedSanitize: SanitizeFn | null = null;

export const LEGAL_CONTENT_ALIASES_BY_PRIORITY = [
  "p_content",
  "legal_policy_content",
  "policy_content",
  "content",
  "legal_setting_value",
  "setting_value",
  "value",
  "description",
  "text",
] as const;

export function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function sanitizeRichHtml(value: string) {
  const input = value || "";

  if (cachedSanitize) {
    return cachedSanitize(input);
  }

  const raw = DOMPurify as unknown as
    | { sanitize?: SanitizeFn }
    | ((windowOrDocument?: unknown) => { sanitize?: SanitizeFn });

  if (typeof (raw as { sanitize?: SanitizeFn }).sanitize === "function") {
    const directSanitize = (raw as { sanitize: SanitizeFn }).sanitize;
    cachedSanitize = (dirty: string, options?: Record<string, unknown>) =>
      directSanitize(dirty, options);
    return cachedSanitize(input);
  }

  if (typeof raw === "function") {
    const factory = raw as (windowOrDocument?: unknown) => {
      sanitize?: SanitizeFn;
    };
    const win =
      typeof globalThis !== "undefined"
        ? (globalThis as { window?: unknown }).window
        : undefined;
    const instance = win != null ? factory(win) : factory();

    if (typeof instance?.sanitize === "function") {
      cachedSanitize = (dirty: string, options?: Record<string, unknown>) =>
        instance.sanitize?.(dirty, options) ?? dirty;
      return cachedSanitize(input);
    }
  }

  return escapeHtml(input);
}

export function containsHtmlMarkup(value: string) {
  return /<[a-z][\s\S]*>/i.test(value);
}

function getStringCandidates(
  source: Record<string, unknown>,
  keys: readonly string[],
) {
  const values: string[] = [];

  for (const key of keys) {
    const value = source[key];

    if (typeof value === "string" && value.trim().length > 0) {
      values.push(value);
      continue;
    }

    if (typeof value === "number" || typeof value === "boolean") {
      values.push(String(value));
    }
  }

  return Array.from(new Set(values));
}

export function selectCanonicalLegalContent(
  source: Record<string, unknown>,
  keys: readonly string[] = LEGAL_CONTENT_ALIASES_BY_PRIORITY,
) {
  const candidates = getStringCandidates(source, keys);

  if (candidates.length === 0) {
    return null;
  }

  const richHtmlCandidate = candidates.find((candidate) =>
    containsHtmlMarkup(candidate),
  );

  return richHtmlCandidate ?? candidates[0] ?? null;
}
